/**
 * The phone app (mobile/, T-45) shows this site in a web view. It adds this mark to the web view's user
 * agent (mobile/src/config.ts; the worker checks the same mark, worker/src/auth/mobile-handoff.ts) and
 * gives the page `window.ReactNativeWebView` to send it messages.
 */
export const NATIVE_APP_USER_AGENT_MARK = 'SoftwareFactoryApp/';

/** Messages the page sends the app (mobile/src/App.tsx onMessage). */
export type NativeAppMessage = { type: 'google-sign-in' } | { type: 'push-register' };

/** What the app tells the page when its Google sign-in ends without signing in (cancelled or failed). */
export const NATIVE_SIGN_IN_ENDED_EVENT = 'factory-app-sign-in-ended';

/**
 * The app opening a page (a tapped link or notification). The page cancels the event once it takes it,
 * going through its unsaved-changes guard (hooks/use-native-app-links.ts); uncancelled, the app loads
 * the address itself.
 */
export const NATIVE_OPEN_EVENT = 'factory-app-open';

/** The path to open in the page for the app's address, or null when it's another site's. */
export function pathForNativeOpen(url: unknown, origin: string): string | null {
  if (typeof url !== 'string') return null;
  try {
    const parsed = new URL(url, origin);
    // Files and other /api addresses aren't pages of the app: the app loads them itself.
    if (parsed.origin !== origin || parsed.pathname.startsWith('/api/')) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

/** The app's answer to `push-register`: the Expo push token, or a null token (no permission, not set up). */
export const NATIVE_PUSH_TOKEN_EVENT = 'factory-app-push-token';

export type NativePushToken = { token: string; platform: 'ios' | 'android' };

interface NativeBridge {
  postMessage(message: string): void;
}

function bridge(): NativeBridge | null {
  if (typeof window === 'undefined' || !navigator.userAgent.includes(NATIVE_APP_USER_AGENT_MARK)) return null;
  return (window as Window & { ReactNativeWebView?: NativeBridge }).ReactNativeWebView ?? null;
}

/** Whether this page is inside the phone app. */
export function inNativeApp(): boolean {
  return bridge() !== null;
}

/** Sends the app a message; false outside the app. */
export function postToNativeApp(message: NativeAppMessage): boolean {
  const app = bridge();
  if (!app) return false;
  app.postMessage(JSON.stringify(message));
  return true;
}

/** Calls `handler` when the app's Google sign-in ends without a session; returns the unsubscribe. */
export function onNativeSignInEnded(handler: () => void): () => void {
  window.addEventListener(NATIVE_SIGN_IN_ENDED_EVENT, handler);
  return () => window.removeEventListener(NATIVE_SIGN_IN_ENDED_EVENT, handler);
}

/**
 * Asks the app for this phone's push token. The first time, the app asks the person to allow
 * notifications, so the answer can take a while; null when they say no, outside the app, or after
 * `timeoutMs`.
 */
export function requestNativePushToken(timeoutMs = 120_000): Promise<NativePushToken | null> {
  if (!inNativeApp()) return Promise.resolve(null);
  return new Promise((resolve) => {
    const done = (value: NativePushToken | null) => {
      window.removeEventListener(NATIVE_PUSH_TOKEN_EVENT, onAnswer);
      clearTimeout(timer);
      resolve(value);
    };
    const onAnswer = (event: Event) => {
      const detail = (event as CustomEvent<Partial<NativePushToken> | null>).detail;
      const ok = detail && typeof detail.token === 'string' && (detail.platform === 'ios' || detail.platform === 'android');
      done(ok ? { token: detail.token as string, platform: detail.platform as NativePushToken['platform'] } : null);
    };
    const timer = setTimeout(() => done(null), timeoutMs);
    window.addEventListener(NATIVE_PUSH_TOKEN_EVENT, onAnswer);
    postToNativeApp({ type: 'push-register' });
  });
}
